import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Input,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../../components/Common";
import { SystemAdminDto, PaginateQuery } from "../../../interfaces";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
  TableSpinner,
  TextColumn,
} from "../../../components";
import { ActionTypeEnum } from "../../../enums";
import DeleteModal from "../../../components/Common/DeleteModal";
import { AddEditSystemAdminModal } from "./AddEditSystemAdminModal";
import {
  useGetSystemAdminUsersQuery,
  useDeleteSystemAdminUserMutation,
} from "../../../store/api/systemAdminApi";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";

type TProps = {};
export const SystemAdminsPage: React.FC<TProps> = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const initState = new PaginateQuery();

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [queryReady, setQueryReady] = useState(false);

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();

  const {
    data: response,
    isLoading: loading,
    refetch,
  } = useGetSystemAdminUsersQuery(paginateQuery, { skip: !queryReady });

  const [
    deleteSystemAdminUser,
    {
      isLoading: deleteSystemAdminUserLoading,
      error: deleteSystemAdminUserError,
    },
  ] = useDeleteSystemAdminUserMutation();

  document.title = `${t("documentTitle", { pageName: "systemAdmins" })}`;

  const columns: TableColumn<SystemAdminDto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn text={row.name[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="primaryPhoneNumber" />,
      cell: (row) => (
        <a
          style={{ cursor: "pointer" }}
          href={`tel:+${row.phoneObj?.countryCode}${row.phoneObj?.phoneNumber}`}
        >
          <u dir="ltr">{`+${row.phoneObj?.countryCode}${row.phoneObj?.phoneNumber}`}</u>
        </a>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="secondaryPhoneNumber" />,
      cell: (row) => (
        <>
          {row.secondaryPhoneCountryCode && row.secondaryPhonePhoneNumber ? (
            <a
              style={{ cursor: "pointer" }}
              href={`tel:+${row.secondaryPhoneCountryCode}${row.secondaryPhonePhoneNumber}`}
            >
              <u dir="ltr">{`+${row.secondaryPhoneCountryCode}${row.secondaryPhonePhoneNumber}`}</u>
            </a>
          ) : (
            "-"
          )}
        </>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="educationSystems" />,
      cell: (row) => {
        const systems = row.educationSystems || [];
        if (!systems.length) {
          return <TextColumn text={t("noEducationSystemsAssigned")} />;
        }
        const langKey = i18n.language === "ar" ? "ar" : "en";
        const labels = systems
          .slice(0, 2)
          .map((s) => s.value?.[langKey] || s.value?.en)
          .join(", ");
        const more =
          systems.length > 2 ? ` +${systems.length - 2}` : "";
        return <TextColumn text={`${labels}${more}`} />;
      },
      sortable: false,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <ActionButton
            onAction={() => onEdit(row.id)}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          <ActionButton
            onAction={() => onDelete(row.id)}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "name.en":
      case "name.ar":
        sortBy = 1;
        break;
      default:
        sortBy = 2;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("created_at:DESC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    const educationSystemId =
      queryParams.get("filter[educationSystemId]") || "";
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: educationSystemId ? { educationSystemId } : {},
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string),
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
    setQueryReady(true);
  }, []);

  useEffect(() => {
    if (!queryReady) return;
    const { limit, page, search, sortBy, filter } = paginateQuery;
    let query = `?page=${page || 1}`;
    if (limit) query += `&limit=${limit}`;
    if (sortBy) query += `&sortBy=${sortBy}`;
    if (search) query += `&search=${search}`;
    if (filter?.educationSystemId)
      query += `&filter[educationSystemId]=${filter.educationSystemId}`;
    if (query !== history.location.search) {
      history.replace({ search: query });
    }
  }, [paginateQuery, queryReady]);

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteSystemAdminUser(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {}
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<SystemAdminDto>,
    sortDirection: string,
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("systemAdmins")} pageTitle={t("cpUsers")} />
          <Alert color="info">
            <ul>
              <li>{t("roles.systemAdmin.line1")}</li>
              <li>{t("roles.systemAdmin.line2")}</li>
            </ul>
          </Alert>
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={3} className="mb-2">
                      <Input
                        type="select"
                        value={paginateQuery.filter?.educationSystemId || ""}
                        onChange={(e) =>
                          setPaginateQuery({
                            ...paginateQuery,
                            page: 1,
                            filter: {
                              ...paginateQuery.filter,
                              educationSystemId: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="">{t("allEducationSystems")}</option>
                        {(educationSystems || []).map((s: any) => (
                          <option key={s.id} value={s.id}>
                            {s.value?.[langKey] || s.value?.en}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button color="success" onClick={() => onAdd()}>
                          <i className="ri-add-fill me-1 align-bottom"></i>
                          {t("addItem", { item: "systemAdmin" })}
                        </Button>
                        <Button
                          color="success"
                          onClick={() => {
                            setPaginateQuery({ ...paginateQuery });
                            refetch();
                          }}
                        >
                          {t("refresh")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        theme={"dark"}
                        columns={columns}
                        data={response?.data || []}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={response?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                  <DeleteModal
                    loading={deleteSystemAdminUserLoading}
                    show={showDeleteModal}
                    onDeleteClick={() => onDeleteConfirm()}
                    onCloseClick={() => {
                      setShowDeleteModal(false);
                    }}
                    error={
                      deleteSystemAdminUserError
                        ? (deleteSystemAdminUserError as any)?.data
                            ?.message || "An error occurred"
                        : undefined
                    }
                  />
                  <AddEditSystemAdminModal
                    id={editingItemId}
                    show={showAddEditModal}
                    onCloseClick={() => {
                      setShowAddEditModal(false);
                      setEditingItemId(null);
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};
